import type { WebRTCStats } from './callStats'
import { isDtxTick } from './dtx'

/**
 * A single stats sample with a monotonic timestamp injected at push-time.
 *
 * `ts` is `performance.now()` (milliseconds, browser-monotonic). Backend
 * never sees it — only used here to compute Δt between consecutive ticks
 * for pps/bitrate/loss deltas. Wall-clock time is irrelevant.
 */
export type Tick = WebRTCStats & { ts: number }

/**
 * Ring buffer cap. At 500ms polling cadence this gives 30 minutes of
 * history. Calls longer than 30 minutes silently drop the oldest ticks
 * (last 30 min wins for aggregation). Documented in the plan's Known
 * limitations.
 */
export const TICK_BUFFER_CAP = 3600

export function appendTick(buf: Tick[], tick: Tick): void {
  buf.push(tick)
  if (buf.length > TICK_BUFFER_CAP) {
    buf.splice(0, buf.length - TICK_BUFFER_CAP)
  }
}

/**
 * All aggregate metrics are computed over **voice-only** ticks (DTX excluded)
 * unless noted otherwise. Names omit the `_voiced` suffix for brevity —
 * semantics are documented here and in the backend column comments.
 */
export interface TickAggregates {
  // Driving the v2 quality formula
  rtt_p95_ms: number
  jitter_p95_ms: number
  loss_ratio_avg: number       // cumulative lost / (lost + received) over voice ticks
  loss_high_time_ratio: number // fraction of voice ticks with per-tick loss > 3%
  dtx_ratio: number            // fraction of classified ticks that were DTX

  // Tooltip / diagnostics
  rtt_p50_ms: number
  pps_avg: number              // average packets/sec over voice ticks
  bitrate_avg_kbps: number     // average bitrate over voice ticks

  // Reserved for future "call activity" metric, not used in formula yet
  bitrate_p95_kbps: number
}

interface DerivedTick {
  rtt: number
  jitter: number
  pps: number
  bitrateKbps: number
  packetsDelta: number
  lossDelta: number
  lossRatioTick: number
  isDtx: boolean
}

const PER_TICK_LOSS_HIGH_THRESHOLD = 0.03

/**
 * Compute aggregate quality metrics from a tick buffer.
 *
 * Returns null when there isn't enough voice data to be meaningful:
 *   - empty buffer
 *   - fewer than 5 voice ticks (per-tick noise dominates percentiles)
 *   - all classified ticks are DTX (pure silence — no voice signal to score)
 *
 * The first tick in any segment (start-of-call or post-reconnect) is used
 * only as a delta baseline; it is NOT counted in `ticks_total` and not
 * classified, so `dtx_ratio` stays honest.
 */
export function aggregateTicks(buf: Tick[]): TickAggregates | null {
  if (buf.length < 2) return null

  const derived = deriveTicks(buf)
  if (derived.length === 0) return null

  const voice = derived.filter(t => !t.isDtx)
  if (voice.length < 5) return null

  const dtxCount = derived.length - voice.length
  const dtxRatio = derived.length === 0 ? 0 : dtxCount / derived.length
  if (dtxRatio === 1) return null // gate: pure silence

  const useShortFallback = voice.length < 10

  const rttSorted = voice.map(t => t.rtt).sort((a, b) => a - b)
  const jitterSorted = voice.map(t => t.jitter).sort((a, b) => a - b)
  const bitrateSorted = voice.map(t => t.bitrateKbps).sort((a, b) => a - b)

  // Cumulative loss over voice-only ticks: sum of per-tick lost over sum
  // of (received + lost). Per-tick "received" comes from packetsDelta that
  // we already computed in deriveTicks.
  let voiceLost = 0
  let voiceTotalSent = 0
  let highLossTicks = 0
  let bitrateSum = 0
  let ppsSum = 0
  for (const t of voice) {
    voiceLost += t.lossDelta
    voiceTotalSent += t.lossDelta + t.packetsDelta
    if (t.lossRatioTick > PER_TICK_LOSS_HIGH_THRESHOLD) highLossTicks++
    bitrateSum += t.bitrateKbps
    ppsSum += t.pps
  }
  const lossRatioAvg = voiceTotalSent === 0 ? 0 : voiceLost / voiceTotalSent

  return {
    rtt_p50_ms: percentile(rttSorted, 0.5, useShortFallback),
    rtt_p95_ms: percentile(rttSorted, 0.95, useShortFallback),
    jitter_p95_ms: percentile(jitterSorted, 0.95, useShortFallback),
    loss_ratio_avg: lossRatioAvg,
    loss_high_time_ratio: highLossTicks / voice.length,
    dtx_ratio: dtxRatio,
    pps_avg: ppsSum / voice.length,
    bitrate_avg_kbps: bitrateSum / voice.length,
    bitrate_p95_kbps: percentile(bitrateSorted, 0.95, useShortFallback),
  }
}

/**
 * Walk the tick buffer once, computing per-tick deltas. The first tick in
 * a segment (or after a counter reset) is used only as a baseline and is
 * not emitted — it stays out of `ticks_total`, keeping `dtx_ratio` honest.
 *
 * Reset is triggered when ANY of the three monotonic counters
 * (packetsReceived, bytesReceived, packetsLost) goes backward. This catches
 * SSRC change on ICE-restart and any partial-reset edge in the browser.
 */
function deriveTicks(buf: Tick[]): DerivedTick[] {
  const out: DerivedTick[] = []
  let baseline: Tick | null = null

  for (const cur of buf) {
    if (baseline === null) {
      baseline = cur
      continue
    }

    const dt = cur.ts - baseline.ts
    const counterReset =
      cur.packetsReceived < baseline.packetsReceived ||
      cur.bytesReceived < baseline.bytesReceived ||
      cur.packetsLost < baseline.packetsLost

    if (counterReset) {
      baseline = cur
      continue
    }
    if (dt <= 0) {
      // Same timestamp (browser throttling) — skip, keep baseline so the
      // next real tick can compute against the original baseline.
      continue
    }

    const seconds = dt / 1000
    const packetsDelta = cur.packetsReceived - baseline.packetsReceived
    const bytesDelta = cur.bytesReceived - baseline.bytesReceived
    const lossDelta = cur.packetsLost - baseline.packetsLost

    const pps = packetsDelta / seconds
    const bitrateKbps = (bytesDelta * 8) / dt
    const total = packetsDelta + lossDelta
    const lossRatioTick = total === 0 ? 0 : lossDelta / total

    out.push({
      rtt: cur.rtt,
      jitter: cur.jitter,
      pps,
      bitrateKbps,
      packetsDelta,
      lossDelta,
      lossRatioTick,
      isDtx: isDtxTick({ pps, lossDelta, bitrateKbps }),
    })

    baseline = cur
  }

  return out
}

/**
 * Linear-interpolated percentile on a pre-sorted ascending array.
 * For voice<10 ticks we fall back to max (p>=0.5) / median (p===0.5) — the
 * percentile shape is too noisy on tiny samples to be useful.
 */
function percentile(sorted: number[], p: number, shortFallback: boolean): number {
  if (sorted.length === 0) return 0
  if (sorted.length === 1) return sorted[0]

  if (shortFallback) {
    if (p === 0.5) {
      const mid = sorted.length / 2
      if (sorted.length % 2 === 0) {
        return (sorted[mid - 1] + sorted[mid]) / 2
      }
      return sorted[Math.floor(mid)]
    }
    // For p95 on short series, use max — single outlier should not be
    // smoothed away on a 6-tick sample.
    return sorted[sorted.length - 1]
  }

  // Linear interpolation between closest ranks
  const rank = p * (sorted.length - 1)
  const lo = Math.floor(rank)
  const hi = Math.ceil(rank)
  if (lo === hi) return sorted[lo]
  const w = rank - lo
  return sorted[lo] * (1 - w) + sorted[hi] * w
}
